import type {
  ContactConversationMessage,
  EchoflicksSessionMedia,
  EchoflicksSessionMessage,
  EchoflicksVideoSession,
  WhatsAppButtonReply,
  WhatsAppMessageButton,
} from "../../types";
import type { MediaUrlSource } from "../../echoflicks/useMediaUrl";
import {
  categoryLabel,
  formatEur,
  pricingTypeLabel,
} from "../../echoflicks/echoMessagingCosts";
import { FELIX } from "./felix";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChatSide = "left" | "right";
export type ChatSender = "contact" | "felix" | "operator" | "system";
export type ChatDeliveryStatus =
  | "pending"
  | "sent"
  | "delivered"
  | "read"
  | "failed";

export type ChatMediaKind =
  | "image"
  | "video"
  | "audio"
  | "voice"
  | "document"
  | "sticker"
  | "location"
  | "contact"
  | "unsupported"
  | "unknown";

export interface ChatMedia {
  kind: ChatMediaKind;
  label: string;
  /** Longer, row-specific explanation (e.g. the dashboard's classification
   *  of an `unsupported` payload). Rendered instead of `label` when set. */
  detail?: string | null;
}

/** A WhatsApp reaction (`raw_payload.reaction = { message_id, emoji }`). An
 *  empty emoji means the reaction was removed. */
export interface ChatReaction {
  targetProviderMessageId: string | null;
  emoji: string;
}

export type ChatButtonKind = "reply" | "url" | "flow" | "list" | "unknown";

/** A button the bot attached to an outbound message (reply or CTA link). */
export interface ChatButton {
  kind: ChatButtonKind;
  title: string;
  url: string | null;
  /** Reply payload the bot receives when tapped (e.g. `create`). */
  callbackData: string | null;
}

/**
 * What Meta charged for an outbound message (EMC contract C6, joined by the
 * dashboard API from the message's WhatsApp cost row).
 */
export interface ChatMessagePrice {
  /** pending | charged | free | undelivered | failed | unpriced. */
  billingState: string | null;
  /** Meta `pricing.type` (regular, free_customer_service, free_entry_point). */
  pricingType: string | null;
  /** Meta `pricing.category` (service, utility, marketing, ...). */
  category: string | null;
  eur: number | null;
  usd: number | null;
}

/** The option the contact tapped when an inbound row answers a button/list. */
export interface ChatButtonReply {
  kind: "reply" | "list" | "template";
  id: string | null;
  title: string | null;
}

/** A single message, normalized from either conversation or session sources. */
export interface ChatMessage {
  id: string;
  sender: ChatSender;
  side: ChatSide;
  senderName: string | null;
  text: string | null;
  media: ChatMedia | null;
  status: ChatDeliveryStatus | null;
  errorText: string | null;
  timestamp: string | null;
  isCommand: boolean;
  command: string | null;
  /** Normalized echo/session key. `null` means an idle/intake message that is
   *  not part of any echo (e.g. before the first `/create`). */
  echoKey: string | null;
  /** WhatsApp media id from the inbound message's `raw_payload` (e.g.
   *  `image.id`), used to join the message to its stored `uploaded_media`. */
  mediaId: string | null;
  /** Resolved source for rendering media inline (signed via `useMediaUrl`).
   *  `null` until/unless resolvable — the bubble falls back to a placeholder. */
  mediaSource: MediaUrlSource | null;
  /** Durable dashboard media row backing this message. */
  mediaItem: EchoflicksSessionMedia | null;
  /** WhatsApp `wamid` of this message, used to attach reactions. */
  providerMessageId: string | null;
  /** Emoji reactions received on this message (one per reacting sender). */
  reactions: string[];
  /** When this row *is* a reaction it never renders as a bubble; the timeline
   *  folds it into the target message's `reactions` instead. */
  reaction: ChatReaction | null;
  /** External link carried by the message (e.g. a maps URL for a location). */
  linkUrl: string | null;
  /** Bot handling outcome for inbound rows (`fallback_*`, `blocked_*`, …). */
  handlingOutcome: string | null;
  /** "none" | "canned" | "flow" — the kind of reply the bot produced. */
  botReplyKind: string | null;
  /** Meta `unsupported` rows: `album_header` | `unavailable` | `unknown`. */
  unsupportedKind: string | null;
  /** Meta error code behind an `unsupported` row (131051, 131060, …). */
  unsupportedCode: string | null;
  /** Media items WhatsApp delivered right after an album header. */
  albumMediaCount: number | null;
  /** Buttons under an outbound bubble, in the order WhatsApp rendered them. */
  buttons: ChatButton[];
  /** Set when the contact answered by tapping a button or list option. */
  buttonReply: ChatButtonReply | null;
  /** Meta's price for an outbound message; absent where the API has none. */
  price?: ChatMessagePrice | null;
}

/** Lightweight summary of an echo, keyed by its WhatsApp session id. */
export interface EchoSummary {
  id: string | number;
  sessionId: string | null;
  title: string | null;
  status: string | null;
  rating: number | null;
}

export type EchoOutcome = "delivered" | "cancelled" | "failed" | "closed";

export type ChatTimelineItem =
  | { type: "date"; id: string; label: string }
  | {
      type: "echo-start";
      id: string;
      echoKey: string;
      title: string | null;
      status: string | null;
      rating: number | null;
      echoId: string | number | null;
    }
  | {
      type: "echo-end";
      id: string;
      echoKey: string;
      title: string | null;
      outcome: EchoOutcome;
      echoId: string | number | null;
    }
  | {
      type: "message";
      id: string;
      message: ChatMessage;
      groupedWithPrev: boolean;
      showSenderName: boolean;
    };

export interface BuildTimelineOptions {
  echoIndex?: Map<string, EchoSummary>;
  /** Reference "now" used for Today/Yesterday labels (injectable for tests). */
  now?: Date;
  locale?: string;
  /** When false, skip echo-window dividers entirely (e.g. the echo detail
   *  view, which already represents a single window). Defaults to true. */
  segmentEchoes?: boolean;
  /** Stored uploaded media used to resolve inline images for media messages,
   *  joined by the WhatsApp media id embedded in each item's `s3_key`. */
  mediaItems?: EchoflicksSessionMedia[];
}

// ---------------------------------------------------------------------------
// Field-level normalization helpers
// ---------------------------------------------------------------------------

const MEDIA_LABELS: Record<ChatMediaKind, string> = {
  image: "Photo",
  video: "Video",
  audio: "Audio",
  voice: "Voice message",
  document: "Document",
  sticker: "Sticker",
  location: "Location",
  contact: "Contact",
  unsupported: "Unsupported message",
  unknown: "Attachment",
};

/** Longer explanation shown in the bubble for unsupported payloads. */
export const UNSUPPORTED_MESSAGE_LABEL =
  "Unsupported message (view-once media, poll or edited message) — WhatsApp did not deliver its content";

/** Meta `unsupported` rows the dashboard classified as the album header
 *  WhatsApp emits before a multi-photo send: no content, no action. */
export const UNSUPPORTED_KIND_ALBUM_HEADER = "album_header";

export function albumHeaderText(count?: number | null): string {
  const n = typeof count === "number" && count > 0 ? count : null;
  return n
    ? `📸 Sent an album (${n} ${n === 1 ? "item" : "items"} follow)`
    : "📸 Sent an album";
}

/** Map raw WhatsApp/ML message_type + message_kind to a media descriptor.
 *  Returns null for plain text-like content (text, button, interactive, …). */
export function detectMedia(
  messageType?: string | null,
  messageKind?: string | null,
): ChatMedia | null {
  const type = (messageType ?? "").trim().toLowerCase();
  // Meta reports view-once media, polls and deleted messages as an
  // `unsupported` / `unknown` type — there is no payload to render.
  if (type === "unsupported" || type === "unknown") {
    return { kind: "unsupported", label: MEDIA_LABELS.unsupported };
  }
  if (type === "contacts") {
    return { kind: "contact", label: MEDIA_LABELS.contact };
  }
  const hint = `${messageType ?? ""} ${messageKind ?? ""}`.toLowerCase();
  const kind: ChatMediaKind | null = hint.includes("sticker")
    ? "sticker"
    : hint.includes("voice") || hint.includes("ptt")
      ? "voice"
      : hint.includes("video")
        ? "video"
        : hint.includes("image") || hint.includes("photo")
          ? "image"
          : hint.includes("audio")
            ? "audio"
            : hint.includes("document") ||
                hint.includes("file") ||
                hint.includes("pdf")
              ? "document"
              : hint.includes("location")
                ? "location"
                : hint.includes("vcard") || hint.includes("contact_card")
                  ? "contact"
                  : null;
  if (!kind) return null;
  return { kind, label: MEDIA_LABELS[kind] };
}

/** Map a provider status string to a delivery state for outbound ticks. */
export function normalizeStatus(
  status?: string | null,
): ChatDeliveryStatus | null {
  const value = status?.toLowerCase().trim();
  if (!value) return null;
  // Check failures first: "undelivered" / "rejected" must not match "deliver".
  if (
    value.includes("fail") ||
    value.includes("error") ||
    value.includes("undeliver") ||
    value.includes("reject")
  ) {
    return "failed";
  }
  if (value.includes("read")) return "read";
  if (value.includes("deliver")) return "delivered";
  if (
    value.includes("pending") ||
    value.includes("queued") ||
    value.includes("sending") ||
    value.includes("enqueued") ||
    value.includes("scheduled") ||
    value.includes("accepted")
  ) {
    return "pending";
  }
  if (value.includes("sent") || value.includes("submitted")) return "sent";
  return null;
}

/** Detect slash-commands like `/create`, `/cancel`. */
export function detectCommand(
  text?: string | null,
  messageKind?: string | null,
  messageType?: string | null,
): { isCommand: boolean; command: string | null } {
  const trimmed = text?.trim() ?? "";
  const match = /^\/([a-z_]+)/i.exec(trimmed);
  if (match) {
    return { isCommand: true, command: `/${match[1].toLowerCase()}` };
  }
  const hint = `${messageKind ?? ""} ${messageType ?? ""}`.toLowerCase();
  if (hint.includes("create_command")) {
    return { isCommand: true, command: "/create" };
  }
  if (hint.includes("command")) {
    return { isCommand: true, command: trimmed || null };
  }
  return { isCommand: false, command: null };
}

/** Normalize a session id into an echo key. Contact-level pseudo sessions
 *  (`contact-<user>`) and empty values are treated as idle (null). */
export function echoKeyOf(sessionId?: string | number | null): string | null {
  if (sessionId == null) return null;
  const value = String(sessionId).trim();
  if (!value) return null;
  if (/^contact[-_]/i.test(value)) return null;
  return value;
}

type RawPayload = Record<string, unknown> | null | undefined;

/** Locate the media sub-object inside a raw WhatsApp message payload, e.g. the
 *  `image` / `video` / `document` node (keyed by the payload's `type`). */
function rawPayloadMediaNode(
  rawPayload: RawPayload,
): Record<string, unknown> | null {
  if (!rawPayload || typeof rawPayload !== "object") return null;
  const type =
    typeof rawPayload.type === "string" ? (rawPayload.type as string) : null;
  const keys = [
    type,
    "image",
    "video",
    "audio",
    "voice",
    "document",
    "sticker",
  ].filter((key): key is string => Boolean(key));
  for (const key of keys) {
    const node = rawPayload[key];
    if (node && typeof node === "object") {
      return node as Record<string, unknown>;
    }
  }
  return null;
}

/** WhatsApp media id from an inbound message's `raw_payload` (e.g. `image.id`).
 *  This is embedded in the stored object's `s3_key`, so it joins a message to
 *  its uploaded media. */
export function extractWhatsAppMediaId(rawPayload: RawPayload): string | null {
  const id = rawPayloadMediaNode(rawPayload)?.id;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

/** A directly-usable media URL if the payload carries one (demo links, or a
 *  WhatsApp `link`). Real WhatsApp media has no durable link, so production
 *  relies on the media-id → s3 join instead. */
export function extractWhatsAppMediaUrl(rawPayload: RawPayload): string | null {
  const node = rawPayloadMediaNode(rawPayload);
  const link = node?.link ?? node?.url;
  return typeof link === "string" && /^(https?:|data:)/.test(link)
    ? link
    : null;
}

/** True when the bot could not act on an inbound message (fallback replies,
 *  blocked flows, ignored payloads, errors) — the "Felix didn't act on this"
 *  badge and the inbox both key off this. */
export function isUnhandledOutcome(outcome?: string | null): boolean {
  const value = (outcome ?? "").trim().toLowerCase();
  if (!value) return false;
  return (
    value.startsWith("fallback_") ||
    value.startsWith("blocked_") ||
    value.startsWith("ignored_") ||
    value.startsWith("error")
  );
}

/** Reaction descriptor from a `reaction` message payload, or null. */
export function extractReaction(
  messageType: string | null | undefined,
  rawPayload: RawPayload,
): ChatReaction | null {
  const node =
    rawPayload && typeof rawPayload.reaction === "object"
      ? (rawPayload.reaction as Record<string, unknown>)
      : null;
  const type = (messageType ?? "").trim().toLowerCase();
  const payloadType =
    typeof rawPayload?.type === "string" ? rawPayload.type.toLowerCase() : "";
  if (!node && type !== "reaction" && payloadType !== "reaction") return null;
  const target = node?.message_id ?? node?.messageId;
  const emoji = node?.emoji;
  return {
    targetProviderMessageId:
      typeof target === "string" && target.trim() ? target.trim() : null,
    emoji: typeof emoji === "string" ? emoji.trim() : "",
  };
}

function numberish(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** `📍 lat, lng` text + a Google Maps link when the payload carries a
 *  `location` node. */
export function extractLocation(
  rawPayload: RawPayload,
): { text: string; linkUrl: string } | null {
  const node =
    rawPayload && typeof rawPayload.location === "object"
      ? (rawPayload.location as Record<string, unknown>)
      : null;
  if (!node) return null;
  const lat = numberish(node.latitude ?? node.lat);
  const lng = numberish(node.longitude ?? node.lng ?? node.lon);
  if (lat === null || lng === null) return null;
  const name = typeof node.name === "string" ? node.name.trim() : "";
  const coords = `${lat}, ${lng}`;
  return {
    text: name ? `📍 ${name} (${coords})` : `📍 ${coords}`,
    linkUrl: `https://maps.google.com/?q=${lat},${lng}`,
  };
}

/** Comma-separated formatted names from a `contacts` payload, or null. */
export function extractContactsText(rawPayload: RawPayload): string | null {
  const list = rawPayload?.contacts;
  if (!Array.isArray(list)) return null;
  const names = list
    .map((entry) => {
      if (!entry || typeof entry !== "object") return "";
      const record = entry as Record<string, unknown>;
      const name =
        record.name && typeof record.name === "object"
          ? (record.name as Record<string, unknown>)
          : null;
      const formatted = name?.formatted_name;
      if (typeof formatted === "string" && formatted.trim()) {
        return formatted.trim();
      }
      return [name?.first_name, name?.last_name]
        .filter((part): part is string => typeof part === "string" && !!part)
        .join(" ")
        .trim();
    })
    .filter(Boolean);
  return names.length ? `👤 ${names.join(", ")}` : null;
}

function directMediaSource(url: string): MediaUrlSource {
  return {
    url,
    storage_bucket: null,
    storage_path: null,
    storage_is_public: false,
  };
}

function mediaSourceFromItem(item: EchoflicksSessionMedia): MediaUrlSource {
  return {
    url: item.url ?? null,
    storage_bucket: item.storage_bucket ?? item.bucket ?? null,
    storage_path: item.storage_path ?? item.s3_key ?? null,
    storage_is_public: item.storage_is_public ?? false,
  };
}

function inferSenderSide(
  direction?: string | null,
  role?: string | null,
): { sender: ChatSender; side: ChatSide } {
  const dir = direction?.toLowerCase();
  const r = role?.toLowerCase();
  if (dir === "inbound") return { sender: "contact", side: "left" };
  if (dir === "outbound") {
    return r === "operator"
      ? { sender: "operator", side: "right" }
      : { sender: "felix", side: "right" };
  }
  // No explicit direction — infer from role.
  if (r === "user") return { sender: "contact", side: "left" };
  if (r === "operator") return { sender: "operator", side: "right" };
  if (r === "system") return { sender: "system", side: "left" };
  return { sender: "felix", side: "right" };
}

function senderNameFor(
  sender: ChatSender,
  contactName?: string | null,
): string | null {
  if (sender === "felix") return FELIX.name;
  if (sender === "operator") return "Operator";
  if (sender === "contact") return contactName?.trim() || "Contact";
  return null;
}

// ---------------------------------------------------------------------------
// Source → ChatMessage adapters
// ---------------------------------------------------------------------------

export function normalizeConversationMessage(
  message: ContactConversationMessage,
  contactName?: string | null,
): ChatMessage {
  const { sender, side } = inferSenderSide(message.direction, message.role);
  const rawPayload = message.raw_payload as RawPayload;
  const rawText = message.text ?? message.body ?? null;
  const location = extractLocation(rawPayload);
  const contactsText = extractContactsText(rawPayload);
  const text = rawText?.trim()
    ? rawText
    : (location?.text ?? contactsText ?? rawText);
  const media = detectMedia(message.message_type, message.message_kind);
  const { isCommand, command } = detectCommand(
    text,
    message.message_kind,
    message.message_type,
  );
  const directUrl = media ? extractWhatsAppMediaUrl(rawPayload) : null;
  const unsupportedKind = message.unsupported_kind?.trim() || null;
  const albumMediaCount =
    typeof message.album_media_count === "number"
      ? message.album_media_count
      : null;
  if (
    media?.kind === "unsupported" &&
    unsupportedKind === UNSUPPORTED_KIND_ALBUM_HEADER
  ) {
    // The header WhatsApp sends before a batch of photos: fold it into a
    // quiet system pill — the photos themselves are the next bubbles.
    return {
      id: String(message.id),
      sender: "system",
      side: "left",
      senderName: null,
      text: albumHeaderText(albumMediaCount),
      media: null,
      status: null,
      errorText: null,
      timestamp: message.occurred_at ?? null,
      isCommand: false,
      command: null,
      echoKey: echoKeyOf(message.session_id),
      mediaId: null,
      mediaSource: null,
      mediaItem: null,
      providerMessageId: message.provider_message_id?.trim() || null,
      reactions: [],
      reaction: null,
      linkUrl: null,
      handlingOutcome: null,
      botReplyKind: null,
      unsupportedKind,
      unsupportedCode: message.unsupported_code?.trim() || null,
      albumMediaCount,
      buttons: [],
      buttonReply: null,
    };
  }
  const explainedMedia =
    media?.kind === "unsupported"
      ? {
          ...media,
          detail:
            message.unsupported_label?.trim() || UNSUPPORTED_MESSAGE_LABEL,
        }
      : media;
  return {
    id: String(message.id),
    sender,
    side,
    senderName: senderNameFor(sender, contactName),
    text,
    media: explainedMedia,
    status: side === "right" ? normalizeStatus(message.provider_status) : null,
    errorText: message.provider_error_details ?? null,
    timestamp: message.occurred_at ?? null,
    isCommand,
    command,
    echoKey: echoKeyOf(message.session_id),
    mediaId: media ? extractWhatsAppMediaId(rawPayload) : null,
    mediaSource: directUrl ? directMediaSource(directUrl) : null,
    mediaItem: null,
    providerMessageId: message.provider_message_id?.trim() || null,
    reactions: [],
    reaction: extractReaction(message.message_type, rawPayload),
    linkUrl: location?.linkUrl ?? null,
    handlingOutcome: message.handling_outcome ?? null,
    botReplyKind: message.bot_reply_kind ?? null,
    unsupportedKind,
    unsupportedCode: message.unsupported_code?.trim() || null,
    albumMediaCount,
    buttons: normalizeButtons(message.buttons, message.button_titles),
    buttonReply:
      side === "left"
        ? normalizeButtonReply(message.button_reply, message.message_type, text)
        : null,
  };
}

export function normalizeSessionMessage(
  message: EchoflicksSessionMessage,
  contactName?: string | null,
): ChatMessage {
  const { sender, side } = inferSenderSide(undefined, message.role);
  const text = message.text ?? null;
  const media = detectMedia(message.message_type, null);
  const { isCommand, command } = detectCommand(
    text,
    null,
    message.message_type,
  );
  const rawPayload = message.raw_payload as RawPayload;
  const directUrl = media ? extractWhatsAppMediaUrl(rawPayload) : null;
  return {
    id: String(message.id),
    sender,
    side,
    senderName: senderNameFor(sender, contactName),
    text,
    media,
    // EMC-074: the dashboard API joins Meta's status and price onto outbound
    // rows (contract C6). Inbound rows carry no ticks, as in WhatsApp.
    status: side === "right" ? normalizeStatus(message.provider_status) : null,
    errorText: null,
    timestamp: message.occurred_at ?? null,
    isCommand,
    command,
    echoKey: echoKeyOf(message.session_id),
    mediaId: media ? extractWhatsAppMediaId(rawPayload) : null,
    mediaSource: directUrl ? directMediaSource(directUrl) : null,
    mediaItem: null,
    providerMessageId: message.provider_message_id?.trim() || null,
    reactions: [],
    reaction: null,
    linkUrl: null,
    handlingOutcome: null,
    botReplyKind: null,
    unsupportedKind: null,
    unsupportedCode: null,
    albumMediaCount: null,
    buttons: normalizeButtons(message.buttons, message.button_titles),
    buttonReply:
      side === "left"
        ? normalizeButtonReply(message.button_reply, message.message_type, text)
        : null,
    price: side === "right" ? sessionMessagePrice(message) : null,
  };
}

/** Meta's price of an outbound session message, or null when it has none. */
export function sessionMessagePrice(
  message: Pick<
    EchoflicksSessionMessage,
    | "billing_state"
    | "pricing_type"
    | "pricing_category"
    | "cost_eur"
    | "cost_usd"
  >,
): ChatMessagePrice | null {
  const price: ChatMessagePrice = {
    billingState: message.billing_state?.trim().toLowerCase() || null,
    pricingType: message.pricing_type?.trim().toLowerCase() || null,
    category: message.pricing_category?.trim().toLowerCase() || null,
    eur: numberish(message.cost_eur),
    usd: numberish(message.cost_usd),
  };
  return price.billingState ||
    price.pricingType ||
    price.eur != null ||
    price.usd != null
    ? price
    : null;
}

export type MessagePriceChip = {
  label: string;
  title: string;
  tone: "charged" | "free" | "muted";
};

function formatUsdAmount(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

/**
 * The chip under an outbound bubble: "€0.0166" for a charged message, "Free
 * tier" or "Free entry point" for a free one, "Not charged" when it never
 * reached the phone. Null while the message waits for its delivery receipt
 * (the clock or single tick already says so) or when there is no price.
 */
export function describeMessagePrice(
  price: ChatMessagePrice | null | undefined,
): MessagePriceChip | null {
  if (!price) return null;
  const category = price.category
    ? `Meta ${categoryLabel(price.category).toLowerCase()} message`
    : "Meta message";
  const state =
    price.billingState ??
    (price.pricingType?.startsWith("free")
      ? "free"
      : price.pricingType === "regular"
        ? "charged"
        : null);
  switch (state) {
    case "charged": {
      const amount =
        price.eur != null
          ? formatEur(price.eur)
          : price.usd != null
            ? formatUsdAmount(price.usd)
            : null;
      if (!amount) {
        return {
          label: "Charged",
          title: `${category}, charged`,
          tone: "charged",
        };
      }
      return {
        label: amount,
        title: `${category}, charged ${amount}${
          price.eur != null && price.usd != null
            ? ` (${formatUsdAmount(price.usd)})`
            : ""
        }`,
        tone: "charged",
      };
    }
    case "free": {
      const label = price.pricingType?.startsWith("free_")
        ? pricingTypeLabel(price.pricingType)
        : "Free";
      return { label, title: `${category}, free (${label})`, tone: "free" };
    }
    case "failed":
    case "undelivered":
      return {
        label: "Not charged",
        title: `${category}, never delivered, so Meta does not charge it`,
        tone: "muted",
      };
    case "unpriced":
      return {
        label: "Unpriced",
        title: `${category}: delivered, but the ledger has no price for it yet`,
        tone: "muted",
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

const BUTTON_KINDS: ReadonlySet<string> = new Set<ChatButtonKind>([
  "reply",
  "url",
  "flow",
  "list",
  "unknown",
]);

const cleanString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/**
 * Buttons the bot attached to an outbound message. Structured `buttons`
 * (kind/title/url/callback) win; rows indexed from older bots only carry
 * `button_titles`, which render as plain buttons of unknown kind.
 */
export function normalizeButtons(
  buttons?: WhatsAppMessageButton[] | null,
  titles?: string[] | null,
): ChatButton[] {
  const structured = (Array.isArray(buttons) ? buttons : []).flatMap(
    (button): ChatButton[] => {
      const title = cleanString(button?.title);
      if (!title) return [];
      const kind = BUTTON_KINDS.has(button.kind)
        ? (button.kind as ChatButtonKind)
        : "unknown";
      return [
        {
          kind,
          title,
          url: cleanString(button.url),
          callbackData: cleanString(button.callback_data),
        },
      ];
    },
  );
  if (structured.length > 0) return structured;
  return (Array.isArray(titles) ? titles : []).flatMap(
    (title): ChatButton[] => {
      const clean = cleanString(title);
      return clean
        ? [{ kind: "unknown", title: clean, url: null, callbackData: null }]
        : [];
    },
  );
}

const BUTTON_REPLY_MESSAGE_TYPES = new Set(["interactive", "button"]);

/**
 * The option a contact tapped. Rows indexed before button replies were
 * projected still identify a tap by their `interactive`/`button` message type
 * and carry the tapped title as their text.
 */
export function normalizeButtonReply(
  reply: WhatsAppButtonReply | null | undefined,
  messageType: string | null | undefined,
  text: string | null,
): ChatButtonReply | null {
  const id = cleanString(reply?.id);
  const title = cleanString(reply?.title);
  if (id || title) {
    const kind =
      reply?.kind === "list" || reply?.kind === "template"
        ? reply.kind
        : "reply";
    return { kind, id, title };
  }
  const legacyTitle = cleanString(text);
  if (
    messageType &&
    BUTTON_REPLY_MESSAGE_TYPES.has(messageType) &&
    legacyTitle
  ) {
    return {
      kind: messageType === "button" ? "template" : "reply",
      id: null,
      title: legacyTitle,
    };
  }
  return null;
}

/**
 * Fold reaction rows into their target message. Reactions are matched by the
 * target's `providerMessageId`; each reacting sender holds at most one emoji
 * (a later reaction replaces it, an empty emoji removes it). Reactions whose
 * target is not in the loaded window become a system-style "reacted 👍" pill;
 * removals of unmatched targets vanish. Input must be in chronological order.
 * Does not mutate the input messages.
 */
export function attachReactions(messages: ChatMessage[]): ChatMessage[] {
  const byProviderId = new Map<string, ChatMessage>();
  for (const message of messages) {
    if (message.reaction || !message.providerMessageId) continue;
    if (!byProviderId.has(message.providerMessageId)) {
      byProviderId.set(message.providerMessageId, message);
    }
  }
  const reactionsByTarget = new Map<string, Map<string, string>>();
  const kept: ChatMessage[] = [];
  for (const message of messages) {
    const reaction = message.reaction;
    if (!reaction) {
      kept.push(message);
      continue;
    }
    const target = reaction.targetProviderMessageId
      ? byProviderId.get(reaction.targetProviderMessageId)
      : undefined;
    if (target) {
      let senders = reactionsByTarget.get(target.id);
      if (!senders) {
        senders = new Map();
        reactionsByTarget.set(target.id, senders);
      }
      if (reaction.emoji) senders.set(message.sender, reaction.emoji);
      else senders.delete(message.sender);
      continue;
    }
    if (!reaction.emoji) continue;
    kept.push({
      ...message,
      sender: "system",
      side: "left",
      senderName: null,
      text: `${message.senderName ?? "Contact"} reacted ${reaction.emoji}`,
      media: null,
      status: null,
      reaction: null,
    });
  }
  return kept.map((message) => {
    const senders = reactionsByTarget.get(message.id);
    return senders && senders.size
      ? { ...message, reactions: [...senders.values()] }
      : message;
  });
}

/** Build the `session_id → EchoSummary` index used to label/link echo windows. */
export function buildEchoIndex(
  sessions: EchoflicksVideoSession[],
): Map<string, EchoSummary> {
  const index = new Map<string, EchoSummary>();
  for (const session of sessions) {
    const key = echoKeyOf(session.session_id);
    if (!key || index.has(key)) continue;
    index.set(key, {
      id: session.id,
      sessionId: session.session_id ?? null,
      title: session.movie_title ?? session.script_title ?? null,
      status: session.status ?? null,
      rating: session.rating ?? null,
    });
  }
  return index;
}

// ---------------------------------------------------------------------------
// Timeline assembly + echo-window segmentation
// ---------------------------------------------------------------------------

function parseTime(value: string | null): number {
  if (!value) return Number.NaN;
  return Date.parse(value);
}

const MAX_LEGACY_MEDIA_MATCH_DISTANCE_MS = 10 * 60 * 1000;

function storedMediaKind(
  item: EchoflicksSessionMedia,
): "image" | "video" | null {
  const hint =
    `${item.mime_type ?? ""} ${item.storage_path ?? item.s3_key ?? item.local_path ?? item.url ?? ""}`.toLowerCase();
  if (hint.includes("video/") || /\.(mp4|m4v|mov|webm)(?:$|[?#])/.test(hint)) {
    return "video";
  }
  if (
    hint.includes("image/") ||
    /\.(jpe?g|png|gif|webp|heic|heif|avif)(?:$|[?#])/.test(hint)
  ) {
    return "image";
  }
  return null;
}

function isCompatibleStoredMedia(
  message: ChatMessage,
  item: EchoflicksSessionMedia,
): boolean {
  const kind = storedMediaKind(item);
  return kind !== null && kind === message.media?.kind;
}

function storedMediaId(item: EchoflicksSessionMedia): string | null {
  const direct = item.media_id?.trim();
  if (direct) return direct;
  const metadataId = item.raw_metadata?.media_id;
  return typeof metadataId === "string" && metadataId.trim()
    ? metadataId.trim()
    : null;
}

function attachStoredMedia(message: ChatMessage, item: EchoflicksSessionMedia) {
  message.mediaItem = item;
  // Durable dashboard storage must replace the temporary Meta webhook URL.
  message.mediaSource = mediaSourceFromItem(item);
}

function resolveStoredMedia(
  messages: ChatMessage[],
  mediaItems: EchoflicksSessionMedia[],
) {
  const used = new Set<string>();
  const mediaKey = (item: EchoflicksSessionMedia) => String(item.id);

  // Prefer an exact WhatsApp media-id match when newer projections preserve it.
  for (const message of messages) {
    if (!message.media || !message.mediaId) continue;
    const messageMediaId = message.mediaId;
    const match = mediaItems.find((item) => {
      if (used.has(mediaKey(item)) || !isCompatibleStoredMedia(message, item)) {
        return false;
      }
      const path = item.s3_key ?? item.storage_path ?? "";
      return (
        storedMediaId(item) === messageMediaId || path.includes(messageMediaId)
      );
    });
    if (!match) continue;
    used.add(mediaKey(match));
    attachStoredMedia(message, match);
  }

  // Legacy rows store image_1.jpg/image_2.jpg without the WhatsApp media id.
  // Pair them one-to-one by closest event time, within a deliberately bounded
  // window, so an unrelated upload cannot be pulled into this message.
  for (const message of messages) {
    if (!message.media || message.mediaItem) continue;
    const messageTime = parseTime(message.timestamp);
    if (Number.isNaN(messageTime)) continue;

    let closest: EchoflicksSessionMedia | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const item of mediaItems) {
      if (used.has(mediaKey(item)) || !isCompatibleStoredMedia(message, item)) {
        continue;
      }
      const itemTime = parseTime(item.created_at_source ?? null);
      if (Number.isNaN(itemTime)) continue;
      const distance = Math.abs(itemTime - messageTime);
      if (distance < closestDistance) {
        closest = item;
        closestDistance = distance;
      }
    }
    if (!closest || closestDistance > MAX_LEGACY_MEDIA_MATCH_DISTANCE_MS) {
      continue;
    }
    used.add(mediaKey(closest));
    attachStoredMedia(message, closest);
  }
}

function outcomeFromStatus(status?: string | null): EchoOutcome {
  const value = status?.toLowerCase();
  if (!value) return "closed";
  if (
    value.includes("complete") ||
    value.includes("deliver") ||
    value.includes("succeed")
  ) {
    return "delivered";
  }
  if (value.includes("cancel")) return "cancelled";
  if (value.includes("fail") || value.includes("error")) return "failed";
  return "closed";
}

function dateLabel(value: string | null, now: Date, locale?: string): string {
  const time = parseTime(value);
  if (Number.isNaN(time)) return "";
  const date = new Date(time);
  const startOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayMs = 86_400_000;
  const diffDays = Math.round((startOf(now) - startOf(date)) / dayMs);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) {
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  }).format(date);
}

/** Clock time shown inside a bubble, e.g. "14:05". */
export function formatClockTime(value: string | null, locale?: string): string {
  const time = parseTime(value);
  if (Number.isNaN(time)) return "";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(time));
}

/**
 * Assemble normalized messages into a flat, render-ready timeline with date
 * separators and echo-window dividers.
 *
 * Echo segmentation strategy:
 * - **session mode** (preferred): any message carries a real echo key
 *   (`session_id` that is not a `contact-*` pseudo session). Messages are
 *   bracketed by their `echoKey`; idle messages (null key) sit outside windows.
 * - **create-fallback mode**: no session ids exist, but `/create` commands do.
 *   Each `/create` opens a synthetic window that stays open until `/cancel`,
 *   `/done`, or the next `/create` — the classic "between /create commands"
 *   heuristic, used only when the backend gave us no session grouping.
 * - **flat mode**: neither signal present → a plain continuous chat.
 */
export function buildConversationTimeline(
  messages: ChatMessage[],
  options: BuildTimelineOptions = {},
): ChatTimelineItem[] {
  const echoIndex = options.echoIndex ?? new Map<string, EchoSummary>();
  const now = options.now ?? new Date();
  const locale = options.locale;

  const ordered = attachReactions(
    messages
      .map((message, index) => ({ message, index }))
      .sort((a, b) => {
        const ta = parseTime(a.message.timestamp);
        const tb = parseTime(b.message.timestamp);
        if (Number.isNaN(ta) && Number.isNaN(tb)) return a.index - b.index;
        if (Number.isNaN(ta)) return 1;
        if (Number.isNaN(tb)) return -1;
        if (ta === tb) return a.index - b.index;
        return ta - tb;
      })
      .map((entry) => entry.message),
  );

  // Resolve inline media to durable dashboard storage. Current projections can
  // preserve the WhatsApp media id; legacy rows are paired by bounded time.
  const mediaItems = options.mediaItems ?? [];
  if (mediaItems.length) {
    resolveStoredMedia(ordered, mediaItems);
  }

  const segmentEchoes = options.segmentEchoes !== false;
  const hasSessionKey =
    segmentEchoes && ordered.some((message) => message.echoKey != null);
  const hasCreateCommand =
    segmentEchoes && ordered.some((message) => message.command === "/create");

  // Resolve the effective echo key per message according to the active mode.
  let resolvedKeys: (string | null)[];
  if (hasSessionKey) {
    resolvedKeys = ordered.map((message) => message.echoKey);
  } else if (hasCreateCommand) {
    let active: string | null = null;
    resolvedKeys = ordered.map((message) => {
      if (message.command === "/create") active = `create:${message.id}`;
      const key = active;
      if (message.command === "/cancel" || message.command === "/done") {
        active = null;
      }
      return key;
    });
  } else {
    resolvedKeys = ordered.map(() => null);
  }

  const items: ChatTimelineItem[] = [];
  let currentDate: string | null = null;
  let currentEcho: string | null = null;
  let prevSender: ChatSender | null = null;

  const openEcho = (key: string) => {
    const summary = echoIndex.get(key) ?? null;
    items.push({
      type: "echo-start",
      id: `echo-start-${key}`,
      echoKey: key,
      title: summary?.title ?? null,
      status: summary?.status ?? null,
      rating: summary?.rating ?? null,
      echoId: summary?.id ?? null,
    });
  };

  const closeEcho = (key: string) => {
    const summary = echoIndex.get(key) ?? null;
    items.push({
      type: "echo-end",
      id: `echo-end-${key}`,
      echoKey: key,
      title: summary?.title ?? null,
      outcome: outcomeFromStatus(summary?.status),
      echoId: summary?.id ?? null,
    });
  };

  ordered.forEach((message, index) => {
    const echoKey = resolvedKeys[index];
    let boundary = false;

    // Date separator (independent of echo windows — an echo may span midnight).
    const label = dateLabel(message.timestamp, now, locale);
    if (label && label !== currentDate) {
      items.push({ type: "date", id: `date-${index}-${label}`, label });
      currentDate = label;
      boundary = true;
    }

    // Echo window transitions.
    if (echoKey !== currentEcho) {
      if (currentEcho != null) closeEcho(currentEcho);
      if (echoKey != null) openEcho(echoKey);
      currentEcho = echoKey;
      boundary = true;
    }

    const isSystem = message.sender === "system";
    const groupedWithPrev =
      !boundary && !isSystem && prevSender === message.sender;
    items.push({
      type: "message",
      id: `message-${message.id}`,
      message,
      groupedWithPrev,
      showSenderName: !groupedWithPrev && !isSystem,
    });
    prevSender = isSystem ? null : message.sender;
  });

  if (currentEcho != null) closeEcho(currentEcho);

  return items;
}
